import { useDispatch, useSelector } from 'react-redux';
import api from './axiosInstance';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  setAnalyticsError,
  setAnalyticsLoading,
  setEmptyHistoryData,
  setFeedback1Rating,
  setFeedback2Rating,
  setFeedback3Rating,
  setFeedbackComments,
  setHistoryData,
  setHistoryLoading,
  setOverAllRating,
  setOverAllRatingResult
} from '../redux/adminDashboardSlice';
import { useRef } from 'react';
import axios from 'axios';

dayjs.extend(utc);

export const useAdminDashboardService = () => {
  const dispatch = useDispatch();
  const {
    selectedModel,
    selectedRating,
    selectedEmail,
    selectedFont,
    selectedDateRange,
    currentPage,
    includeMyTeam
  } = useSelector((state) => state.adminDashboard);

  const abortControllerRef = useRef(null);
  let cancelToken;

  const fetchFilteredData = async (page = null, prompt = '', signal) => {
    const params = new URLSearchParams();

    if (selectedEmail.length !== 0) params.append('email', selectedEmail.join(','));
    if (selectedRating.length !== 0) params.append('rating', selectedRating.join(','));
    if (selectedModel.length !== 0) params.append('modelName', selectedModel.join(','));
    if (selectedFont.length !== 0) params.append('fontmd5', selectedFont.join(','));
    if (prompt) params.append('prompt', prompt);
    if (
      selectedDateRange.startDate &&
      selectedDateRange.endDate &&
      selectedDateRange.startDate !== selectedDateRange.endDate
    ) {
      params.append(
        'startDate',
        dayjs(selectedDateRange.startDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
      );
      params.append(
        'endDate',
        dayjs(selectedDateRange.endDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
      );
    }

    const pageNumber = page || 1;
    params.append('page', pageNumber);
    params.append('pageSize', 50);

    try {
      dispatch(setHistoryLoading(true));
      const response = await api.get(
        `/user-history?${params.toString()}`,
        signal ? { signal } : {}
      );
      if (pageNumber === 1) dispatch(setEmptyHistoryData());
      dispatch(
        setHistoryData({
          hsitoryData: response.data?.data,
          totalDocuments: response.data?.totalDocuments,
          totalPages: response.data?.totalPages,
          currentPage: response.data?.currentPage
        })
      );
    } catch (error) {
      if (error.name === 'AbortError') {
        dispatch(setHistoryLoading(true));
        return;
      } else {
        console.log(error?.response?.data);
      }
    } 
  };

  const buildRequestBody = () => ({
    email: selectedEmail.length !== 0 ? selectedEmail : [],
    modelName: selectedModel.length !== 0 ? selectedModel : [],
    fontmd5: selectedFont.length !== 0 ? selectedFont : [],
    startDate:
      selectedDateRange.startDate &&
      selectedDateRange.endDate &&
      selectedDateRange.startDate !== selectedDateRange.endDate
        ? dayjs(selectedDateRange.startDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
        : null,
    endDate:
      selectedDateRange.startDate &&
      selectedDateRange.endDate &&
      selectedDateRange.startDate !== selectedDateRange.endDate
        ? dayjs(selectedDateRange.endDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
        : null,
    includeTTTeam: selectedEmail.length > 0 ? false : includeMyTeam
  });

  const fetchData = async (endpoint, successAction) => {
    try {
      dispatch(setAnalyticsLoading(true));
      const bodyData = buildRequestBody();
      const response = await api.post(endpoint, bodyData);
      dispatch(successAction(response.data));
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Something went wrong!';
      dispatch(setAnalyticsError(errorMessage));
    } finally {
      dispatch(setAnalyticsError(false));
    }
  };

  const fetchOverAllRating = () => fetchData('/analytics/overAlllRating', setOverAllRating);

  const fetchOverAllRatingResult = () =>
    fetchData('/analytics/overAllRatingResult', setOverAllRatingResult);

  const fetchFeedbackComments = () => fetchData('/analytics/feedbackComments', setFeedbackComments);

  const fetchFeedback1Rating = () => fetchData('/analytics/feedback1Rating', setFeedback1Rating);

  const fetchFeedback2Rating = () => fetchData('/analytics/feedback2Rating', setFeedback2Rating);

  const fetchFeedback3Rating = () => fetchData('/analytics/feedback3Rating', setFeedback3Rating);

  return {
    fetchFilteredData,
    fetchOverAllRating,
    fetchOverAllRatingResult,
    fetchFeedbackComments,
    fetchFeedback1Rating,
    fetchFeedback2Rating,
    fetchFeedback3Rating
  };
};
